import React, { useState, useEffect, useContext, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { baseURL, baseURLnoapi, powerStatsAPI, CURRENT_SEASON } from "../Constants";
import "../styles/components/match-page.scss";
import { FaGamepad } from "react-icons/fa";
import { AuthContext } from "../context/AuthContext";
import PlayedMatchView from "../components/PlayedMatchView";
import UpcomingMatchView from "../components/UpcomingMatchView";
import Get5MatchControl from "../components/match/Get5MatchControl";
import MatchHero from "../components/match/MatchHero";
import { get5MatchService } from "../services/get5MatchService";
import { matchService } from "../services/matchService";
import { parseVetoLog } from "../utils/vetoParser";
import { LiveMatchWebhookProvider } from "../context/LiveMatchWebhookContext";
import LiveMatchView from "../components/LiveMatchView";
import highlightsService from "../services/highlightsService";
import { MatchHighlights } from "../types/highlights.types";
import { analytics } from "../services/analytics";

// Define mappools for each season
const seasonMappools: Record<number, string[]> = {
	1: ["Ancient", "Anubis", "Dust2", "Inferno", "Mirage", "Nuke", "Vertigo"],
	2: ["Ancient", "Anubis", "Dust2", "Inferno", "Mirage", "Nuke", "Train"],
	3: ["Ancient", "Dust2", "Inferno", "Mirage", "Nuke", "Overpass", "Train"],
};

// Helper function to get the current season's mappool
const getSeasonMappool = (season: number): string[] => {
	return seasonMappools[season] || seasonMappools[CURRENT_SEASON]; // Default to current season if season not found
};

// Types
export interface Team {
	teamId: string;
	teamName: string;
	teamLogo: string;
	ownerId: string | null;
	season: number;
	division: string;
	twitter: string | null;
	coachID: string | null;
	website: string | null;
	seed: string;
	playoffSeed: string;
	score?: number; // Used for finished matches
}

export interface Match {
	challonge_id: string;
	team1: Team;
	team2: Team;
	isPlayoff: boolean;
	playoffPlayed: boolean;
	division: string;
	match_time: string;
	stream_url: string | null;
	veto: any | null;
	roundNumber: number | null;
	season: number;
}

export interface PlayerStats {
	steamID: string;
	playerName: string;
	avatarUrl?: string;
	name?: string;
	role?: string;
	team?: { id: string; name: string; logo: string };
	kills?: number;
	deaths?: number;
	assists?: number;
	adr?: number;
	kdr?: number;
	hltv2Rating?: number;
	hsPercentage?: number;
	kast?: number;
	firstKills?: number;
	rating?: number;
	account?: {
		steamID: string;
		name: string;
		playerName: string;
		role: string | null;
	};
}

// API response interface
export interface VetoMapStats {
	mapName: string;
	amountPicked: number;
	amountBanned: number;
	amountPlayed: number;
	amountWon: number;
	chosenCTPercentage: number;
}

// Combined stats for UI display
export interface CombinedMapStats {
	mapName: string;
	team1: VetoMapStats;
	team2: VetoMapStats;
}

interface MatchState {
	status: "upcoming" | "played" | "live";
	currentMap?: string;
	currentMapNumber?: number; // For BO3: which map is currently being played (1, 2, or 3)
	team1Score?: number;
	team2Score?: number;
	currentRound?: number;
	maxRounds?: number;
	hasIncompleteData?: boolean; // Flag for broken/incomplete match data
	// BO3-specific properties
	bo3?: boolean; // Is this a BO3 match?
	bo3InProgress?: boolean; // For BO3: indicates series is in progress (between maps)
	waitingForMap?: number; // For BO3: which map number we're waiting to create (2 or 3)
	maps?: any[]; // For BO3 played: array of all map data
	seriesScore?: { team1: number; team2: number }; // For BO3: series score (e.g., 2-1)
	isViewingLiveMap?: boolean; // Are we currently viewing the live map (vs a finished map)?
	liveMapNumber?: number; // Which map number is actually live (for BO3)
}

// Helper function to convert short team IDs (like "69") to UUID format
const shortIdToUuid = (shortId: string): string => {
	// If already a UUID, return as-is
	if (shortId.includes('-')) {
		return shortId;
	}
	// Convert short ID to UUID format: 00000000-0000-0000-0000-000000000069
	const paddedId = shortId.padStart(12, '0');
	return `00000000-0000-0000-0000-${paddedId}`;
};

// Helper function to get team logo URL with fallback based on season
// For season 1, optionally pass cached logo URLs from backend
export const getTeamLogoUrl = (
	logoUrl: string,
	teamId: string,
	season: number,
	season1LogoCache?: Record<string, string>
) => {
	const teamPlaceholderImage =
		"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23333'/%3E%3Ctext x='50' y='50' font-size='18' text-anchor='middle' alignment-baseline='middle' font-family='sans-serif' fill='%23fff'%3ENo Logo%3C/text%3E%3C/svg%3E";

	// For season 1, check cache first (fetched from backend with correct logo URLs)
	if (season === 1 && season1LogoCache && teamId) {
		// Try direct lookup first
		if (season1LogoCache[teamId]) {
			return season1LogoCache[teamId];
		}
		// Try converting short ID to UUID format
		const uuidId = shortIdToUuid(teamId);
		if (season1LogoCache[uuidId]) {
			return season1LogoCache[uuidId];
		}
		// Fallback to placeholder if not in cache
		return teamPlaceholderImage;
	}

	// For current season, use the specified format
	if (season === CURRENT_SEASON) {
		return baseURLnoapi + `/images/teams/${teamId}.png`;
	}

	// For other seasons, use standard logic
	if (logoUrl && logoUrl.startsWith("http")) {
		return logoUrl;
	} else if (logoUrl) {
		return `${baseURLnoapi}${logoUrl}`;
	} else {
		return teamPlaceholderImage;
	}
};

// Function to get avatar URL and handle fallbacks
export const getAvatarUrl = (steamID: string): string => {
	if (!steamID) {
		return "";
	}
	return `${baseURLnoapi}/images/players/${steamID}.png`;
};

// FIXED: Remove early return for single matches and use header team IDs as reference
const normalizeMatchDataForHeader = (
	matches: any[],
	headerTeam1Id: string,
	headerTeam2Id: string
): any[] => {
	// Use header team IDs as the reference for correct positioning
	const referenceTeam1Id = headerTeam1Id;
	const referenceTeam2Id = headerTeam2Id;

	// Find reference team info from the matches
	const referenceTeam1Info =
		matches.find((m) => m.team1.teamId === headerTeam1Id)?.team1 ||
		matches.find((m) => m.team2.teamId === headerTeam1Id)?.team2;
	const referenceTeam2Info =
		matches.find((m) => m.team1.teamId === headerTeam2Id)?.team1 ||
		matches.find((m) => m.team2.teamId === headerTeam2Id)?.team2;

	const normalizedMatches = matches.map((match) => {
		// Check if team IDs match the reference positions
		const team1IdMatchesReference = match.team1.teamId === referenceTeam1Id;
		const team2IdMatchesReference = match.team2.teamId === referenceTeam2Id;

		// Check if team names are consistent with their IDs
		const team1NameCorrect = referenceTeam1Info
			? match.team1.teamName === referenceTeam1Info.teamName
			: true;
		const team2NameCorrect = referenceTeam2Info
			? match.team2.teamName === referenceTeam2Info.teamName
			: true;

		// Case 1: Team IDs are in correct positions but names/logos are wrong
		if (
			team1IdMatchesReference &&
			team2IdMatchesReference &&
			referenceTeam1Info &&
			referenceTeam2Info &&
			(!team1NameCorrect || !team2NameCorrect)
		) {
			const normalizedMatch = {
				...match,
				team1: {
					...match.team1,
					teamName: referenceTeam1Info.teamName,
					teamLogo: referenceTeam1Info.teamLogo,
				},
				team2: {
					...match.team2,
					teamName: referenceTeam2Info.teamName,
					teamLogo: referenceTeam2Info.teamLogo,
				},
				// Keep team1won as-is since IDs are correct
			};

			return normalizedMatch;
		}

		// Case 2: Team IDs are swapped (original logic)
		else if (
			!team1IdMatchesReference &&
			!team2IdMatchesReference &&
			referenceTeam1Info &&
			referenceTeam2Info
		) {
			const normalizedMatch = {
				...match,
				team1: {
					...match.team2,
					teamName: referenceTeam1Info.teamName,
					teamLogo: referenceTeam1Info.teamLogo,
				},
				team2: {
					...match.team1,
					teamName: referenceTeam2Info.teamName,
					teamLogo: referenceTeam2Info.teamLogo,
				},
				team1playerids: match.team2playerids,
				team2playerids: match.team1playerids,
				team1score: match.team2score,
				team2score: match.team1score,
				team1won: !match.team1won, // This is the key part for series score
			};

			return normalizedMatch;
		}

		return match;
	});

	return normalizedMatches;
};

// Function to calculate series score by team ID (not by position)
const calculateSeriesScoreByTeamId = (
	normalizedMatches: any[],
	referenceTeam1Id: string,
	referenceTeam2Id: string
): { team1Wins: number; team2Wins: number } => {
	let team1Wins = 0;
	let team2Wins = 0;

	normalizedMatches.forEach((match, index) => {
		// Determine who actually won by looking at team1won and team1's ID
		if (match.team1won) {
			// team1 won this map
			if (match.team1.teamId === referenceTeam1Id) {
				team1Wins++;
			} else {
				team2Wins++;
			}
		} else {
			// team2 won this map
			if (match.team2.teamId === referenceTeam1Id) {
				team1Wins++;
			} else {
				team2Wins++;
			}
		}
	});

	return { team1Wins, team2Wins };
};

// Function to format date and time
export const formatDateTime = (dateString: string) => {
	const date = new Date(dateString);

	// Format: "May 11, 2025 • 11:24 AM"
	return (
		date.toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
		}) +
		" • " +
		date.toLocaleTimeString("en-US", {
			hour: "numeric",
			minute: "2-digit",
			hour12: true,
		})
	);
};

// Helper function to determine win rate class
export const getWinRateClass = (winRate: number): string => {
	if (isNaN(winRate)) return "medium";
	if (winRate >= 0.65) return "high";
	if (winRate <= 0.35) return "low";
	return "medium";
};

const MatchPage: React.FC = () => {
	const { matchId } = useParams<{ matchId: string }>();
	const { isLoggedinAdmin } = useContext(AuthContext);

	// State variables
	const [match, setMatch] = useState<Match | null>(null);
	const [team1Players, setTeam1Players] = useState<PlayerStats[]>([]);
	const [team2Players, setTeam2Players] = useState<PlayerStats[]>([]);
	const [mapStats, setMapStats] = useState<CombinedMapStats[]>([]);
	const [matchState, setMatchState] = useState<MatchState>({
		status: "upcoming",
	});
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);
	const [playedMatchData, setPlayedMatchData] = useState<any>(null);
	const [highlightsData, setHighlightsData] = useState<MatchHighlights | null>(null);
	const [season1TeamLogos, setSeason1TeamLogos] = useState<Record<string, string>>({});

	// Helper functions for demo download
	const hasMatchDemo = () => {
		return (
			playedMatchData &&
			Array.isArray(playedMatchData) &&
			playedMatchData.length > 0 &&
			playedMatchData.some(
				(game: any) => game.downloadLinks && game.downloadLinks.trim() !== ""
			)
		);
	};

	const getAvailableDownloads = (): string[] => {
		if (!playedMatchData || !Array.isArray(playedMatchData)) {
			return [];
		}

		return playedMatchData
			.filter(
				(game: any) => game.downloadLinks && game.downloadLinks.trim() !== ""
			)
			.map((game: any) => game.downloadLinks.trim());
	};

	const handleDownloadAllDemos = () => {
		const downloads = getAvailableDownloads();
		downloads.forEach((downloadUrl: string, index: number) => {
			// Stagger downloads to avoid overwhelming the browser
			setTimeout(() => {
				handleDemoDownload(downloadUrl);
			}, index * 1000); // 1 second delay between each download
		});
	};

	const handleDemoDownload = (downloadUrl: string) => {
		// Method 1: Create a temporary iframe (most reliable)
		const iframe = document.createElement("iframe");
		iframe.style.display = "none";
		iframe.src = downloadUrl;
		document.body.appendChild(iframe);

		// Clean up after 5 seconds
		setTimeout(() => {
			document.body.removeChild(iframe);
		}, 5000);

		// Method 2: Fallback - open in new window without referrer
		// window.open(downloadUrl, '_blank', 'noopener,noreferrer');
	};

	// Function to check if match is played by using the dedicated API
	const checkIfMatchPlayed = async (
		matchId: string
	): Promise<{ isPlayed: boolean; data: any }> => {
		try {
			const response = await axios.get(
				`https://api.rising.powerstats.dk/RisingStats/api/Match/${matchId}`
			);
			// If the API returns data, the match is played
			if (response.data && Object.keys(response.data).length > 0) {
				setPlayedMatchData(response.data);
				return { isPlayed: true, data: response.data };
			}
			return { isPlayed: false, data: null };
		} catch (err) {
			// If there's an error (404 most likely), then the match isn't played yet
			return { isPlayed: false, data: null };
		}
	};

	// Function to determine match status based on API response
	const determineMatchStatus = async (
		matchData: Match
	): Promise<MatchState> => {
		console.log("=== MATCH STATUS DETECTION DEBUG ===");
		console.log("Match ID:", matchId);
		console.log("Match Data:", matchData);

		// FIRST: Detect if this is a BO3 by parsing veto (consistent with Get5MatchControl)
		let isBo3 = false;
		if (matchData.veto && matchData.team1 && matchData.team2) {
			const vetoSteps = parseVetoLog(matchData.veto, matchData.team1, matchData.team2);
			const pickedMaps = vetoSteps.filter(step => step.type === 'pick');
			isBo3 = pickedMaps.length > 1;  // BO3 has 2 picks + 1 decider
			console.log("Is BO3:", isBo3, "- Picked maps:", pickedMaps.length);
		}

		// If BO3, check map-status to understand series state BEFORE checking individual map status
		// SKIP backend check for Season 1 - backend has no map data for historical matches
		if (isBo3 && matchId && matchData.season !== 1) {
			try {
				console.log("BO3 detected - checking map status from backend...");
				const mapStatusResponse = await matchService.getMapStatus(matchId);
				console.log("Map status response:", mapStatusResponse);

				const { maps } = mapStatusResponse;
				const liveMaps = maps.filter((m: any) => m.status === 'live');
				const finishedMaps = maps.filter((m: any) => m.status === 'finished');
				const createdMaps = maps.filter((m: any) => m.status === 'created');

				console.log(`BO3 Status: ${liveMaps.length} live, ${finishedMaps.length} finished, ${createdMaps.length} created, ${maps.length} total`);

				// If any map is live, show live view for that map
				if (liveMaps.length > 0) {
					const liveMap = liveMaps[0];
					console.log("BO3: Map is live -", liveMap);
					return {
						status: "live",
						currentMap: liveMap.map_name,
						currentMapNumber: liveMap.map_number
					};
				}

				// If 2+ maps finished, check if series is complete
				if (finishedMaps.length >= 2) {
					// Check PowerStats for detailed stats of all finished maps
					const result = await checkIfMatchPlayed(matchId);
					if (result.isPlayed && result.data && result.data.length >= 2) {
						console.log("BO3: Series appears complete with", result.data.length, "finished maps");

						// Normalize data to match header team order before calculating scores
						const normalizedData = normalizeMatchDataForHeader(
							result.data,
							matchData.team1.teamId,
							matchData.team2.teamId
						);

						// Calculate series score using normalized data and header team IDs
						const { team1Wins, team2Wins } = calculateSeriesScoreByTeamId(
							normalizedData,
							matchData.team1.teamId,
							matchData.team2.teamId
						);

						console.log(`BO3 Series Score: Team1=${team1Wins}, Team2=${team2Wins}`);

						// If a team has won 2+ maps, series is complete
						if (team1Wins >= 2 || team2Wins >= 2) {
							console.log("BO3: Series is complete!");
							return {
								status: "played",
								bo3: true,
								maps: normalizedData,
								seriesScore: { team1: team1Wins, team2: team2Wins },
								team1Score: team1Wins,
								team2Score: team2Wins
							};
						}
					}

					// Series not complete - waiting for map 3, stay on live view
					console.log("BO3: Series score 1-1 - staying on live view, waiting for map 3");
					return {
						status: "live",  // Keep showing LiveMatchView
						bo3InProgress: true,
						waitingForMap: 3,
						maps: maps  // Pass all maps for selector
					};
				}

				// If 1 map finished, stay on live view and wait for map 2
				if (finishedMaps.length === 1) {
					console.log("BO3: Map 1 finished - staying on live view, waiting for map 2");
					return {
						status: "live",  // Keep showing LiveMatchView
						bo3InProgress: true,
						waitingForMap: 2,
						maps: maps  // Pass all maps for selector
					};
				}

				// No maps finished yet - check if any are created/live
				if (createdMaps.length > 0 || liveMaps.length > 0) {
					console.log("BO3: Maps exist, showing as upcoming");
					return { status: "upcoming" };
				}

				// No maps created yet
				console.log("BO3: No maps created yet");
				return { status: "upcoming" };
			} catch (err) {
				console.error("Error checking BO3 map status:", err);
				// Fall through to regular BO1 logic
			}
		}

		// BO1 logic below - First check Get5 status if we have a match ID
		if (matchId) {
			try {
				console.log("Checking Get5 status for match:", matchId);
				const get5Status = await get5MatchService.checkGet5Status(matchId);
				console.log("Get5 Status Response:", get5Status);

				if (get5Status.exists) {
					console.log("Get5 match exists with status:", get5Status.status);

					// If Get5 status is 'finished', we need to check PowerStats
					// But if PowerStats doesn't have data yet, stay in 'live' state
					if (get5Status.status === 'finished') {
						console.log("Get5 match is finished, checking for detailed stats...");

						// Check PowerStats first
						const result = await checkIfMatchPlayed(matchId || "");
						console.log("Match played check result:", result);

						if (result.isPlayed && result.data && result.data.length > 0) {
							// PowerStats has data - return played status (will be handled below)
							console.log("PowerStats has detailed stats, will show played view");
						} else {
							// PowerStats doesn't have data yet - stay in live state
							console.log("PowerStats doesn't have stats yet, staying in LIVE state with Get5 data");

							try {
								const liveData = await get5MatchService.getLiveMatchData(matchId);
								if (liveData) {
									return {
										status: "live",
										team1Score: liveData.mapStats?.team1Score || 0,
										team2Score: liveData.mapStats?.team2Score || 0,
										currentMap: liveData.mapStats?.mapName || "",
										currentRound: liveData.mapStats?.roundNumber || 1,
										maxRounds: 24 // CS2 max rounds
									};
								}
							} catch (err) {
								console.error("Error fetching Get5 live data:", err);
								// Return basic live state even if we can't fetch data
								return {
									status: "live",
									team1Score: 0,
									team2Score: 0,
									currentMap: "",
									currentRound: 1,
									maxRounds: 24
								};
							}
						}
					}
					// Consider both 'live' and 'created' as live matches
					// 'created' means match is ready but players haven't connected yet
					else if (get5Status.status === 'live' || get5Status.status === 'created') {
						console.log("MATCH IS LIVE/READY! Checking live match data...");

						// Try to get live match data to verify match isn't actually finished
						try {
							const liveData = await get5MatchService.getLiveMatchData(matchId);
							if (liveData) {
								// Check if the match is actually finished despite Get5 status
								if (liveData.match?.status === 'finished') {
									console.log("Live match data shows finished status, skipping to played match check");
									// Continue to played match check below
								} else {
									// Match is truly live
									console.log("Match is confirmed live, returning live status");
									return {
										status: "live",
										team1Score: liveData.mapStats?.team1Score || 0,
										team2Score: liveData.mapStats?.team2Score || 0,
										currentMap: liveData.mapStats?.mapName || "",
										currentRound: liveData.mapStats?.roundNumber || 1,
										maxRounds: 24 // CS2 max rounds
									};
								}
							}
						} catch (err) {
							console.error("Error fetching live match data:", err);
							// If we can't get live data, assume it's still live based on Get5 status
							return { status: "live" };
						}
					}
				} else {
					console.log("No Get5 match found");
				}
			} catch (err) {
				console.error("Error checking Get5 status:", err);
			}
		}

		// Then check if the match is played using the dedicated API
		console.log("Checking if match is played...");
		const result = await checkIfMatchPlayed(matchId || "");
		console.log("Match played check result:", result);

		if (result.isPlayed && result.data && result.data.length > 0) {
			// FIXED: Pass header team IDs to normalization function
			const normalizedData = normalizeMatchDataForHeader(
				result.data,
				matchData.team1.teamId,
				matchData.team2.teamId
			);

			// For BO3, calculate total series score using the new method
			if (normalizedData.length > 1) {
				// Get reference team IDs from the first match
				const referenceTeam1Id = normalizedData[0].team1.teamId;
				const referenceTeam2Id = normalizedData[0].team2.teamId;

				// Calculate series score by team ID
				const { team1Wins, team2Wins } = calculateSeriesScoreByTeamId(
					normalizedData,
					referenceTeam1Id,
					referenceTeam2Id
				);

				// Map scores back to original match object team order
				let originalTeam1Score, originalTeam2Score;

				if (matchData.team1.teamId === referenceTeam1Id) {
					// Original team1 matches normalized team1
					originalTeam1Score = team1Wins;
					originalTeam2Score = team2Wins;
				} else {
					// Original team1 matches normalized team2, so swap scores
					originalTeam1Score = team2Wins;
					originalTeam2Score = team1Wins;
				}

				// Check if it's truly incomplete by examining the series score
				// A BO3 with 2 maps is only incomplete if the score is 1-1 (not 2-0)
				const isIncomplete = normalizedData.length === 2 &&
				                     originalTeam1Score === 1 &&
				                     originalTeam2Score === 1;
				const finalStatus = {
					status: "played" as const,
					team1Score: originalTeam1Score,
					team2Score: originalTeam2Score,
					currentMap: "Best of 3",
					hasIncompleteData: isIncomplete, // Mark if data is incomplete
				};
				console.log("Returning BO3 played status:", finalStatus);
				return finalStatus;
			} else {
				// Single match - use normalized data
				const matchInfo = normalizedData[0];
				const finalStatus = {
					status: "played" as const,
					team1Score: matchInfo.team1score,
					team2Score: matchInfo.team2score,
					currentMap:
						matchInfo.mapName?.replace("de_", "").toUpperCase() || "Mirage",
				};
				console.log("Returning single match played status:", finalStatus);
				return finalStatus;
			}
		}

		// If match is a played playoff match, it's finished
		if (matchData.playoffPlayed && matchData.isPlayoff) {
			const finalStatus = {
				status: "played" as const,
				team1Score: 2,
				team2Score: 1,
				currentMap: "Best of 3",
			};
			console.log("Returning playoff played status:", finalStatus);
			return finalStatus;
		}

		// All other matches are upcoming
		const finalStatus = {
			status: "upcoming" as const,
			team1Score: 0,
			team2Score: 0,
			currentMap: "",
		};
		console.log("Returning upcoming status:", finalStatus);
		return finalStatus;
	};

	// Fetch player data using the appropriate API based on season
	const fetchPlayerData = async (
		teamId: string,
		setSetter: React.Dispatch<React.SetStateAction<PlayerStats[]>>,
		currentMatch: Match
	) => {
		try {
			// Special handling for season 1
			if (currentMatch.season === 1) {
				// Use the legacy endpoint for season 1
				const playersResponse = await axios.get(
					`https://api.rising.powerstats.dk/RisingStats/api/PlayerStat/getPlayersByTeamId?teamid=${teamId}&season=1`
				);

				if (playersResponse.data && Array.isArray(playersResponse.data)) {

					// Get the correct team logo URL based on season
					const teamLogoUrl = getTeamLogoUrl(
						currentMatch.team1.teamId === teamId
							? currentMatch.team1.teamLogo
							: currentMatch.team2.teamLogo,
						teamId,
						currentMatch.season
					);

					// Transform the legacy data format to match our PlayerStats interface
					const players = playersResponse.data.map((player: any) => ({
						steamID: player.steamID,
						playerName:
							player.account?.playerName ||
							player.playerName ||
							`Player ${Math.random().toString(36).substring(7)}`,
						name: player.account?.name || null,
						role: player.account?.role || null,
						avatarUrl: getAvatarUrl(player.steamID),
						kills: player.kills,
						deaths: player.deaths,
						assists: player.assists,
						adr: player.adr,
						kdr: player.kd,
						hltv2Rating: player.hltv2Rating,
						hsPercentage: player.hs,
						kast: player.kast,
						firstKills: player.fk,
						rating: player.hltv2Rating,
						team: {
							id: teamId,
							name:
								player.team?.teamName ||
								(currentMatch.team1.teamId === teamId
									? currentMatch.team1.teamName
									: currentMatch.team2.teamName),
							logo: teamLogoUrl,
						},
					}));

					setSetter(players);
					return;
				}
			} else {
				// For other seasons, use the ncseries API
				const teamResponse = await axios.get(
					`https://api.ncseries.gg/api/Team/${teamId}`
				);

				if (
					teamResponse.data &&
					teamResponse.data.players &&
					Array.isArray(teamResponse.data.players)
				) {
					// Get the correct team logo URL based on season
					const teamLogoUrl = getTeamLogoUrl(
						teamResponse.data.logo ||
							(currentMatch.team1.teamId === teamId
								? currentMatch.team1.teamLogo
								: currentMatch.team2.teamLogo),
						teamId,
						currentMatch.season
					);

					// Transform player data to match our PlayerStats interface
					const players = await Promise.all(
						teamResponse.data.players.map(async (player: any) => {
							try {
								// Try to get player stats if available
								const playerStatResponse = await axios.get(
									`${powerStatsAPI}/PlayerStat/getPlayerBySeasonAndSteamId/?steamId=${
										player.steamID
									}&season=${currentMatch?.season || 1}`
								);

								// Create player object with data from both sources
								return {
									steamID: player.steamID,
									playerName: player.playerName,
									name: player.name,
									role: player.role,
									avatarUrl: getAvatarUrl(player.steamID),
									kills: playerStatResponse.data?.kills,
									deaths: playerStatResponse.data?.deaths,
									assists: playerStatResponse.data?.assists,
									adr: playerStatResponse.data?.adr,
									kdr: playerStatResponse.data?.kd,
									hltv2Rating: playerStatResponse.data?.hltv2Rating,
									hsPercentage: playerStatResponse.data?.hs,
									kast: playerStatResponse.data?.kast,
									firstKills: playerStatResponse.data?.fk,
									rating: playerStatResponse.data?.hltv2Rating || 0,
									team: {
										id: teamId,
										name: teamResponse.data.name,
										logo: teamLogoUrl,
									},
								};
							} catch (err) {
								// Return player with basic info even if stats aren't available
								return {
									steamID: player.steamID,
									playerName: player.playerName,
									name: player.name,
									role: player.role,
									avatarUrl: getAvatarUrl(player.steamID),
									rating: 0,
									team: {
										id: teamId,
										name: teamResponse.data.name,
										logo: teamLogoUrl,
									},
								};
							}
						})
					);

					setSetter(players.filter(Boolean) as PlayerStats[]);
					return;
				}
			}

			// If we get here, neither API approach worked, so fall back to dummy data
			console.warn(
				`Failed to fetch players for team ${teamId}, using dummy data`
			);
			createDummyPlayers(teamId, currentMatch, setSetter);
		} catch (err) {
			console.error(`Error fetching team data for team ${teamId}:`, err);
			createDummyPlayers(teamId, currentMatch, setSetter);
		}
	};

	// Helper function to create dummy players
	const createDummyPlayers = (
		teamId: string,
		currentMatch: Match,
		setSetter: React.Dispatch<React.SetStateAction<PlayerStats[]>>
	) => {
		const teamName =
			currentMatch?.team1.teamId === teamId
				? currentMatch.team1.teamName
				: currentMatch?.team2.teamName || "Team " + teamId;

		// Get correct team logo URL based on match season
		const teamLogo = getTeamLogoUrl(
			currentMatch?.team1.teamId === teamId
				? currentMatch.team1.teamLogo
				: currentMatch.team2.teamLogo,
			teamId,
			currentMatch.season
		);

		const dummyPlayers = Array.from({ length: 5 }, (_, i) => ({
			steamID: `player${i}`,
			playerName: `${teamName} Player ${i + 1}`,
			name: `Player ${i + 1}`,
			role: "Rifler",
			rating: 0,
			team: {
				id: teamId,
				name: teamName,
				logo: teamLogo,
			},
		}));

		setSetter(dummyPlayers);
	};

	// Fetch map data using VetoMapStats directly with season-based mappool
	const fetchMapData = async (currentMatch: Match) => {
		try {

			// Get the current season's mappool
			const currentSeasonMaps = getSeasonMappool(currentMatch.season);

			// Get veto data for both teams
			const team1VetoPromise = axios.get<VetoMapStats[]>(
				`https://api.rising.powerstats.dk/RisingStats/api/Match/getVetoStats?teamid=${currentMatch.team1.teamId}&season=${currentMatch.season}`
			);

			const team2VetoPromise = axios.get<VetoMapStats[]>(
				`https://api.rising.powerstats.dk/RisingStats/api/Match/getVetoStats?teamid=${currentMatch.team2.teamId}&season=${currentMatch.season}`
			);

			const [team1VetoResponse, team2VetoResponse] = await Promise.all([
				team1VetoPromise,
				team2VetoPromise,
			]);

			// Check if we got valid data
			if (team1VetoResponse.data && team2VetoResponse.data) {

				// Create a map lookup for faster access
				const team1VetoMap = new Map<string, VetoMapStats>();
				const team2VetoMap = new Map<string, VetoMapStats>();

				team1VetoResponse.data.forEach((map) =>
					team1VetoMap.set(map.mapName, map)
				);
				team2VetoResponse.data.forEach((map) =>
					team2VetoMap.set(map.mapName, map)
				);

				// FIXED: Use season mappool instead of API data to determine which maps to show
				const combinedMaps: CombinedMapStats[] = currentSeasonMaps.map(
					(mapName) => {
						const team1Data = team1VetoMap.get(mapName) || {
							mapName,
							amountPicked: 0,
							amountBanned: 0,
							amountPlayed: 0,
							amountWon: 0,
							chosenCTPercentage: 0,
						};

						const team2Data = team2VetoMap.get(mapName) || {
							mapName,
							amountPicked: 0,
							amountBanned: 0,
							amountPlayed: 0,
							amountWon: 0,
							chosenCTPercentage: 0,
						};

						return {
							mapName,
							team1: team1Data,
							team2: team2Data,
						};
					}
				);

				setMapStats(combinedMaps);
				return;
			}

			// If API fails, set empty data for current season maps
			console.warn(
				"Failed to fetch veto data, using empty data for season maps"
			);
			const emptyMaps: CombinedMapStats[] = currentSeasonMaps.map(
				(mapName) => ({
					mapName,
					team1: {
						mapName,
						amountPicked: 0,
						amountBanned: 0,
						amountPlayed: 0,
						amountWon: 0,
						chosenCTPercentage: 0,
					},
					team2: {
						mapName,
						amountPicked: 0,
						amountBanned: 0,
						amountPlayed: 0,
						amountWon: 0,
						chosenCTPercentage: 0,
					},
				})
			);

			setMapStats(emptyMaps);
		} catch (err) {
			console.error("Error fetching veto data:", err);

			// If there's an error, still show season maps with empty data
			const currentSeasonMaps = getSeasonMappool(currentMatch.season);
			const emptyMaps: CombinedMapStats[] = currentSeasonMaps.map(
				(mapName) => ({
					mapName,
					team1: {
						mapName,
						amountPicked: 0,
						amountBanned: 0,
						amountPlayed: 0,
						amountWon: 0,
						chosenCTPercentage: 0,
					},
					team2: {
						mapName,
						amountPicked: 0,
						amountBanned: 0,
						amountPlayed: 0,
						amountWon: 0,
						chosenCTPercentage: 0,
					},
				})
			);

			setMapStats(emptyMaps);
		}
	};

	// Load match data
	useEffect(() => {
		// Set default page title
		document.title = 'Match Details | Northern Championships Series';

		const fetchMatchData = async () => {
			setLoading(true);
			try {
				// Fetch match details from the real API
				const matchResponse = await axios.get(
					`${powerStatsAPI}/Match/getUpcomingMatchById/${matchId}`
				);

				if (matchResponse.data) {
					const matchData = matchResponse.data;
					setMatch(matchData);

					// For season 1 matches, fetch team logos from backend
					if (matchData.season === 1) {
						try {
							const teamsResponse = await axios.get(`${baseURL}/Team?season=1`);
							if (teamsResponse.data && Array.isArray(teamsResponse.data)) {
								const logoMap: Record<string, string> = {};
								teamsResponse.data.forEach((team: any) => {
									if (team.id && team.logo) {
										logoMap[team.id] = team.logo;
									}
								});
								setSeason1TeamLogos(logoMap);
							}
						} catch (err) {
							console.error('Failed to fetch season 1 team logos:', err);
						}
					}

					// Update page title with team names
					if (matchData.team1 && matchData.team2) {
						document.title = `${matchData.team1.teamName} vs ${matchData.team2.teamName} | Northern Championships Series`;
					}

					// Determine match status
					const status = await determineMatchStatus(matchData);
					console.log("Final match state being set:", status);
					setMatchState(status);

					// Track match view
					analytics.content.matchViewed(
						matchId || '',
						matchData.division,
						status.status
					);

					// Now fetch player data for both teams
					if (matchData.team1 && matchData.team2) {
						await Promise.all([
							fetchPlayerData(
								matchData.team1.teamId,
								setTeam1Players,
								matchData
							),
							fetchPlayerData(
								matchData.team2.teamId,
								setTeam2Players,
								matchData
							),
						]);

						// After we have team data, fetch map data
						await fetchMapData(matchData);

						// Fetch highlights for played matches
						if (status.status === "played" && matchId) {
							try {
								const highlights = await highlightsService.getMatchHighlights(matchId);
								if (highlights) {
									setHighlightsData(highlights);
									console.log(`Loaded ${highlights.highlightCount} highlights for match ${matchId}`);
								}
							} catch (highlightError) {
								console.error("Error fetching highlights:", highlightError);
								// Non-critical error - don't block the page
							}
						}
					}
				}
			} catch (err) {
				console.error("Error fetching match data:", err);
				setError("Failed to load match information");
			} finally {
				setLoading(false);
			}
		};

		if (matchId) {
			fetchMatchData();
		}
	}, [matchId]);

	// Auto-polling to detect when match goes live
	useEffect(() => {
		// Only poll if match is currently "upcoming"
		if (matchState.status !== "upcoming" || !matchId || !match) {
			return;
		}

		console.log('[MatchPage] Setting up auto-polling for live match detection');

		const checkMatchStatus = async () => {
			try {
				console.log('[MatchPage] Polling for match status...');
				const get5Status = await get5MatchService.checkGet5Status(matchId);

				if (get5Status.exists && (get5Status.status === 'live' || get5Status.status === 'created')) {
					console.log('[MatchPage] Match went live! Transitioning to live view...');

					// Update match state to trigger re-render with live view
					setMatchState({
						status: "live",
						team1Score: 0,
						team2Score: 0,
						currentMap: "",
						currentRound: 1,
						maxRounds: 24
					});
				}
			} catch (err) {
				console.error('[MatchPage] Error polling match status:', err);
			}
		};

		// Poll every 60 seconds
		const pollInterval = setInterval(checkMatchStatus, 60000);

		// Cleanup
		return () => {
			console.log('[MatchPage] Cleaning up auto-polling');
			clearInterval(pollInterval);
		};
	}, [matchState.status, matchId, match]);

	// Memoize the callback to prevent infinite re-renders
	const handleLiveDataUpdate = useCallback((data: {
		team1Score: number;
		team2Score: number;
		currentMap?: string;
		currentRound?: number;
		maxRounds?: number;
	}) => {
		setMatchState(prev => {
			// Check if anything changed
			const hasChanges =
				prev.team1Score !== data.team1Score ||
				prev.team2Score !== data.team2Score ||
				prev.currentMap !== data.currentMap ||
				prev.currentRound !== data.currentRound;

			if (hasChanges) {
				console.log('[MatchPage] Updating match state with live data:', data);
				return {
					...prev,
					status: "live" as const,
					team1Score: data.team1Score,
					team2Score: data.team2Score,
					currentMap: data.currentMap,
					currentRound: data.currentRound,
					maxRounds: data.maxRounds || 24
				};
			}
			return prev;
		});
	}, []);

	// Callback when detailed stats are ready
	const handleDetailedStatsReady = useCallback(async () => {
		console.log('[MatchPage] 🎉 Detailed stats ready! Transitioning to played view...');

		if (!match || !matchId) return;

		try {
			// Re-run the match status detection to get played state
			const status = await determineMatchStatus(match);
			console.log('[MatchPage] New match status:', status);
			setMatchState(status);

			// Also fetch the played match data
			const result = await checkIfMatchPlayed(matchId);
			if (result.isPlayed) {
				console.log('[MatchPage] ✅ Successfully transitioned to played view');
			}
		} catch (err) {
			console.error('[MatchPage] Error transitioning to played view:', err);
		}
	}, [match, matchId]);

	// Callback when selected map changes in LiveMatchView
	const handleMapStateChange = useCallback((mapState: {
		mapName: string;
		mapNumber?: number;
		team1SeriesScore?: number;
		team2SeriesScore?: number;
		team1Score?: number;
		team2Score?: number;
		isLiveMap?: boolean;
		liveMapNumber?: number;
		currentRound?: number;
		maxRounds?: number;
	}) => {
		console.log('[MatchPage] Map state changed:', mapState);
		setMatchState(prev => ({
			...prev,
			currentMap: mapState.mapName,
			currentMapNumber: mapState.mapNumber,
			team1Score: mapState.team1Score,
			team2Score: mapState.team2Score,
			currentRound: mapState.currentRound,
			maxRounds: mapState.maxRounds,
			isViewingLiveMap: mapState.isLiveMap,
			liveMapNumber: mapState.liveMapNumber,
			seriesScore: {
				team1: mapState.team1SeriesScore || 0,
				team2: mapState.team2SeriesScore || 0
			}
		}));
	}, []);

	if (loading) {
		return (
			<div className="match-loading">
				<div className="loading-spinner"></div>
				<h2>Loading match details...</h2>
			</div>
		);
	}

	if (error) {
		return (
			<div className="match-error">
				<h2>Error</h2>
				<p>{error}</p>
				<Link to="/matches" className="btn btn-primary">
					Return to Matches
				</Link>
			</div>
		);
	}

	if (!match) {
		return (
			<div className="match-not-found">
				<h2>Match Not Found</h2>
				<Link to="/matches" className="btn btn-primary">
					Return to Matches
				</Link>
			</div>
		);
	}

	return (
		<div className="match-page">
			{/* Header and upcoming match content stays in container */}
			<div className="container">
				{/* Match Hero Header */}
				<MatchHero
					team1={match.team1}
					team2={match.team2}
					team1Score={matchState.team1Score || null}
					team2Score={matchState.team2Score || null}
					status={matchState.status}
					matchTime={
						matchState.status === "played"
							? (playedMatchData &&
								Array.isArray(playedMatchData) &&
								playedMatchData[0]?.match_time
								? playedMatchData[0].match_time
								: match.match_time)
							: match.match_time
					}
					currentMap={matchState.currentMap}
					currentRound={matchState.currentRound}
					maxRounds={matchState.maxRounds}
					seriesScore={matchState.seriesScore}
					liveMapNumber={matchState.liveMapNumber}
					isViewingLiveMap={matchState.isViewingLiveMap}
					hasIncompleteData={matchState.hasIncompleteData}
					demoDownloadButton={
						matchState.status === "played" && hasMatchDemo() ? (
							<button
								onClick={handleDownloadAllDemos}
								className="demo-button"
								style={{
									background: "none",
									border: "none",
									padding: 0,
									font: "inherit",
									cursor: "pointer",
									color: "inherit",
								}}
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="16"
									height="16"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								>
									<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
									<polyline points="7,10 12,15 17,10" />
									<line x1="12" y1="15" x2="12" y2="3" />
								</svg>
								Download Demo
								{getAvailableDownloads().length > 1 ? "s" : ""}
							</button>
						) : undefined
					}
					getTeamLogoUrl={(logoUrl, teamId, season) => getTeamLogoUrl(logoUrl, teamId, season, season1TeamLogos)}
					formatDateTime={formatDateTime}
				/>

				{/* Admin Veto Button - Only visible to admins */}
				{isLoggedinAdmin && matchState.status === "upcoming" && (
					<div className="admin-actions">
						<a href={`/match/${matchId}/veto`} className="veto-button">
							<FaGamepad />
							<span>Start Map Veto</span>
						</a>
					</div>
				)}

				{/* Get5 Match Control - Only visible to admins and only for upcoming matches */}
				{isLoggedinAdmin && match && matchState.status === "upcoming" && (
					<Get5MatchControl match={match} />
				)}

			{/* Get5 Match Control for Live Matches - For BO3 map creation */}
			{isLoggedinAdmin && match && matchState.status === "live" && (
				<Get5MatchControl match={match} />
			)}
			</div>

			{/* LiveMatchView - Show when match is live */}
			{matchState.status === "live" && match && (
				<LiveMatchWebhookProvider
					matchId={match.challonge_id}
					onScoreUpdate={handleLiveDataUpdate}
					onDetailedStatsReady={handleDetailedStatsReady}
				>
					<LiveMatchView
						match={match}
						onMapStateChange={handleMapStateChange}
						showHero={false}
					/>
				</LiveMatchWebhookProvider>
			)}

			{/* UpcomingMatchView renders outside container to allow wide veto section */}
			{matchState.status === "upcoming" && (
				<UpcomingMatchView
					match={match}
					team1Players={team1Players}
					team2Players={team2Players}
					mapStats={mapStats}
				/>
			)}

			{/* PlayedMatchView renders outside container to allow wide veto section */}
			{matchState.status === "played" && (
				<PlayedMatchView
					match={match}
					matchData={playedMatchData}
					team1Players={team1Players}
					team2Players={team2Players}
					highlightsData={highlightsData}
					season1TeamLogos={season1TeamLogos}
				/>
			)}
		</div>
	);
};

export default MatchPage;
